/**
 * The business-registry answer of a tax-code lookup (VNNIC Registrar API `EnterpriseInfoDTO` /
 * `BusinessInfoDTO`, docs/registries/vnnic-registrar-api-2.2.0.json) as the forms use it: the
 * name to fill, the status (an enterprise not in operation is not used), and the facts staff
 * compare by eye. Names are never split into first / last name (the registry gives one full
 * name); the address is the registry's own, older administrative list, so it is shown, not
 * filled (OT&E finding F16).
 */

import type { VnnicBusinessLookup } from '../api';

export interface RegistryFacts {
  found: boolean;
  /** Enterprise / household business name as registered. */
  name: string | null;
  shortName: string | null;
  englishName: string | null;
  status: string | null;
  /** `false` when the registry says the business is not in operation; `null` when it does not say. */
  active: boolean | null;
  /** Founding / registration date (`YYYY-MM-DD`). */
  foundingDate: string | null;
  /** Legal representative (enterprise) or owner (household), full name as registered. */
  representative: string | null;
  address: string | null;
  /** Household business registration number. */
  registrationCode: string | null;
}

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : null);

const norm = (s: string) => s.normalize('NFC').toLocaleLowerCase('vi').replace(/\s+/g, ' ').trim();

/** Whether a status text means "in operation" (`null`: no status given). */
export function isActiveStatus(status: string | null): boolean | null {
  if (!status) return null;
  const s = norm(status);
  if (/(ngừng|tạm dừng|giải thể|phá sản|thu hồi|chấm dứt|đóng mã|không hoạt động|inactive|dissolved|closed|suspended)/.test(s)) return false;
  if (/(đang hoạt động|active|hoạt động)/.test(s)) return true;
  return null;
}

export function registryFacts(r: VnnicBusinessLookup, household: boolean): RegistryFacts {
  const x = r.found && r.result ? r.result : {};
  const date = (v: unknown) => text(v)?.slice(0, 10) ?? null;
  const status = household ? null : text(x.statusName);
  return {
    found: r.found && Boolean(r.result),
    name: text(household ? x.businessName : x.enterpriseName),
    shortName: text(x.shortName),
    englishName: text(x.englishName),
    status,
    active: isActiveStatus(status),
    foundingDate: date(household ? x.registrationDate : x.foundingDate),
    representative: text(household ? x.ownerName : x.directorName),
    address: text(x.fullAddress) ?? text(x.address),
    registrationCode: household ? text(x.registrationCode) : null,
  };
}

/** Whether a person's last + " " + first name is the registered full name (case, spacing and Unicode form aside). */
export function sameFullName(last: string, first: string, registered: string | null): boolean | null {
  if (!registered || !(last.trim() || first.trim())) return null;
  return norm(`${last} ${first}`) === norm(registered);
}
